import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
} from '@angular/core';
import { AuthService } from '@shared/services/auth.service';
import {
  BehaviorSubject,
  catchError,
  of,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { AlertService } from '@shared/services/alert.service';
import { AsyncPipe } from '@angular/common';
import { TuiLetModule } from '@taiga-ui/cdk';
import { TranslocoPipe } from '@jsverse/transloco';
import { TuiButtonModule } from '@taiga-ui/core';
import { TuiIslandModule } from '@taiga-ui/kit';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { SignIn } from '@store/auth/auth.actions';
import { UserInvite } from '../../../../types/auth';

@Component({
  selector: 'app-invites',
  standalone: true,
  imports: [
    AsyncPipe,
    TuiLetModule,
    TranslocoPipe,
    TuiButtonModule,
    TuiIslandModule,
  ],
  templateUrl: './invites.component.html',
  styleUrl: './invites.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvitesComponent implements OnInit {
  loading$ = new BehaviorSubject(true);
  acceptInProgress$ = new BehaviorSubject(false);

  fetchInvites$ = new Subject<void>();

  invites$ = this.fetchInvites$.pipe(
    startWith(true),
    tap(() => this.loading$.next(true)),
    switchMap(() =>
      this.auth.getInviteDetails().pipe(
        tap(() => this.loading$.next(false)),
        catchError(err => {
          this.loading$.next(false);
          this.alert.showError(err.error);
          return of(null);
        }),
      ),
    ),
  );

  accept$ = new Subject<UserInvite>();
  constructor(
    private readonly auth: AuthService,
    private readonly alert: AlertService,
    private readonly destroyRef: DestroyRef,
    private readonly store: Store,
  ) {}

  onAcceptClick() {
    this.accept$
      .pipe(
        tap(() => this.acceptInProgress$.next(true)),
        switchMap(invite =>
          this.auth.acceptInvite(invite.divisionId).pipe(
            tap(response => {
              this.acceptInProgress$.next(false);
              this.store.dispatch(new SignIn(response));
              this.fetchInvites$.next();
            }),
            catchError(err => {
              this.alert.showError(err.error);
              return of(null);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit() {
    this.onAcceptClick();
  }
}
